import { Boxes } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useResource } from '@retink/web-ui'
import { Badge } from '@retink/web-ui'
import { Card, CardHeader, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'

interface Product {
  id: string
  display_name: string
  description: string | null
  status: string
  health_status: string | null
  checked_at: string | null
}

const statusTone = (s: string) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : s === 'retired' ? 'neutral' : 'neutral'
const healthTone = (s: string | null) =>
  s === 'healthy' ? 'success' : s === 'degraded' ? 'warning' : s === 'down' ? 'danger' : 'neutral'

// Platform operations view: service catalogue + health. Every active service is
// available to every workspace; this is not an onboarding/provisioning screen.
export function Products() {
  const { data: products = [], loading, error, reload } = useResource(() => api.get<Product[]>('/api/admin/products'), [])

  return (
    <div className="p-8">
      <PageHeader
        title="Operations"
        description="Services available across the fleet and their latest health state."
        action={<Link to="/operations/audit" className="text-sm text-primary hover:underline">View activity</Link>}
      />

      <Card className="overflow-hidden">
        <CardHeader>Services</CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['Product', 'Key', 'Status', 'Health', 'Checked'].map((h) => <TableHead key={h}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableEmpty colSpan={5}><LoadingState /></TableEmpty>}
            {!loading && error && <TableEmpty colSpan={5}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {!loading && !error && products.length === 0 && (
              <TableEmpty colSpan={5}>
                <EmptyState
                  icon={Boxes}
                  title="No products"
                  description="The product catalog is empty."
                />
              </TableEmpty>
            )}
            {products.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <div className="font-medium">{p.display_name}</div>
                  {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{p.id}</TableCell>
                <TableCell><Badge tone={statusTone(p.status)}>{p.status}</Badge></TableCell>
                <TableCell><Badge tone={healthTone(p.health_status)}>{p.health_status ?? 'unknown'}</Badge></TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {p.checked_at ? new Date(p.checked_at).toLocaleString() : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}
